import React, { forwardRef, useState, useEffect } from "react";
import { Button, Card, Row, Col, Form, Input, Select } from "antd";
import moment from "moment";
import {
  GET_LOCAL_SUPPLIER_LISTS,
  GET_SUPPLIER_INVOICE_LISTS,
  GET_SUPPLIERS_CHALLAN_LISTS,
  GET_INVOICE_DETAILS,
  INVOICE_CHALLAN_MAPPING_RESOURCE,
  GET_INVOICE_CHALLAN_MAPPING_MRR_NO,
  INVOICE_CHALLAN_MAPPING_DOC_ADD,
} from "../../../../apiServices/API_ENDPOINTS";
import { postData, getData, putData } from "../../../../apiServices/common";
import { alertPop } from "../../../../apiServices/common/helper";
import { isArrayAndHasValue } from "../../../../utils/functions";
import ChallanAssignTable from "./ChallanAssignTable";
import { useParams } from "react-router";
import CustomDatePicker from "common/CustomDatePicker";
import UploadDoc from "../UploadDoc";

const ReconciliationForm = forwardRef((props) => {
  // Props
  const { reconciliationInfo, form, discartForm, isEdit, viewMode } = props;

  const { reconciliationId } = useParams();
  const initialChallanDetails = {
    unique_id: 1,
    challan_id: null,
    receive_date: "",
    item_category: "",
    challan_qty: "",
    challan_value: "",
    unit: "",
  };

  // States
  const [piNumbersList] = useState([]);
  const [filesList, setFileList] = useState([]);
  const [challanDetailsList, setChallanDetailsList] = useState([initialChallanDetails]);

  // new
  const [localSuppliers, setLocalSuppliers] = useState([]);
  const [localSupplierInvoices, setLocalSupplierInvoices] = useState([]);
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [challanList] = useState([]);
  const [diffDetails, setDiffDetails] = useState(null);

  // Available challans for dropdown
  const [availableChallanList, setAvailableChallanList] = useState([]);

  // Selected challan IDs from dropdown
  const [selectedChallanIds, setSelectedChallanIds] = useState([]);

  // Dropdown open state
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Antd Constants
  const { Option } = Select;

  // Effects
  useEffect(() => {
    getLocalSuppliers();
    if (!isEdit && !viewMode) getMrrNo();
  }, []);

  useEffect(() => {
    if (reconciliationInfo) {
      // Extract saved challan IDs from edit API response
      const savedChallanIds = reconciliationInfo?.challans?.map((item) => item.challan_id) || [];
      setSelectedChallanIds(savedChallanIds);

      setChallanDetailsList([]);
      // getLocalSuppliers(reconciliationInfo?.supplier_id);
      getSupplierInvoiceLists(reconciliationInfo?.supplier_id);
      // getSupplierChallanLists(reconciliationInfo?.supplier_id);
      getChallanListByInvoiceSalesContract(
        reconciliationInfo?.supplier_id,
        reconciliationInfo?.sales_contract_id,
        savedChallanIds,
      );
      getInvoiceDetails(reconciliationInfo?.import_details_id);

      form.setFieldsValue({
        mrr_no: reconciliationInfo?.mrr_no,
        formatted_mrr_no: reconciliationInfo?.mrr_no
          ? `${reconciliationInfo?.mrr_prefix}${reconciliationInfo?.mrr_no}`
          : "",
        mrr_date: moment(reconciliationInfo?.mrr_date).format("DD-MMM-YYYY"),
        supplier_id: reconciliationInfo?.supplier_id,
        import_details_id: reconciliationInfo?.import_details_id,
        doc_sending_date: reconciliationInfo?.doc_sending_date ? moment(reconciliationInfo?.doc_sending_date) : null,
        remarks: reconciliationInfo?.remarks,
      });

      // Set existing document (view/edit) into upload table
      if (reconciliationInfo?.documents) {
        const docs = Array.isArray(reconciliationInfo.documents)
          ? reconciliationInfo.documents
          : [reconciliationInfo.documents];

        const mappedDocs = docs
          .filter((doc) => doc && doc.document)
          .map((doc) => ({
            id: doc.id,
            name: doc.document_name,
            path: doc.document,
          }));

        setFileList(mappedDocs);
      } else {
        setFileList([]);
      }
    }
  }, [reconciliationInfo, reconciliationId, isEdit]);

  const onFinishReconciliationForm = async (values) => {
    // remove formatted_mrr_no from values
    delete values.formatted_mrr_no;

    if (!isArrayAndHasValue(selectedChallanIds) || selectedChallanIds.length === 0) {
      alertPop("error", "Please select at least one challan from the dropdown");
      return;
    }

    const bodyData = {
      ...values,
      mrr_date: moment(values?.mrr_date).format("YYYY-MM-DD"),
      doc_sending_date: values?.doc_sending_date ? moment(values?.doc_sending_date).format("YYYY-MM-DD") : null,
      challan_details: challanDetailsList, // Only selected challans from dropdown
    };

    const apiCall = reconciliationId
      ? putData(`${INVOICE_CHALLAN_MAPPING_RESOURCE}/${reconciliationId}`, bodyData)
      : postData(INVOICE_CHALLAN_MAPPING_RESOURCE, bodyData);

    const response = await apiCall;

    if (response) {
      alertPop("success", `Mapping ${reconciliationId ? "Updated" : "Created"} Successfully`);
      form.resetFields();
      discartForm();
    }
  };

  // local purchase reconciliation
  const getLocalSuppliers = async (filterValues) => {
    const bodyData = {
      ...filterValues,
    };

    const response = await getData(GET_LOCAL_SUPPLIER_LISTS, false, bodyData);
    if (response && response?.data?.code === 200) {
      setLocalSuppliers(response?.data?.data);
    }
  };

  const getSupplierInvoiceLists = async (value) => {
    form.resetFields([
      "import_details_id",
      "buyer_name",
      "position_type",
      "b2b_lc_number",
      "goods_description",
      "import_date",
      "invoice_qty",
      "invoice_number",
      "invoice_value",
      "sales_contract_id",
      "sales_contract_number",
      "status",
      "documents_handover_date",
    ]);
    const query = isEdit
      ? `${GET_SUPPLIER_INVOICE_LISTS}/${value}?edit=1&map_id=${reconciliationId}`
      : `${GET_SUPPLIER_INVOICE_LISTS}/${value}`;

    const response = await getData(query);

    if (response && response?.data?.code === 200) {
      setLocalSupplierInvoices(response?.data?.data || []);
    }
  };

  const getChallanListByInvoiceSalesContract = async (supplier_id, salesContractId, savedChallanIds = []) => {
    if (!salesContractId) return;

    const query =
      isEdit && reconciliationId
        ? `${GET_SUPPLIERS_CHALLAN_LISTS}/${supplier_id}/${salesContractId}?edit=1&map_id=${reconciliationId}`
        : `${GET_SUPPLIERS_CHALLAN_LISTS}/${supplier_id}/${salesContractId}`;

    const response = await getData(query);
    if (response && response?.data?.code === 200) {
      const challanData = response?.data?.data || [];

      if (!isArrayAndHasValue(challanData)) {
        alertPop("error", "No challans found for this supplier and sales contract");
        setAvailableChallanList([]);
        setChallanDetailsList([]);
        setSelectedChallanIds([]);
        return;
      }

      // Set available challans for dropdown (all available challans)
      setAvailableChallanList(challanData);

      // In edit/view mode: Show only saved/selected challans in table
      // In add mode: Show empty table (user will select from dropdown)
      if ((isEdit || viewMode) && savedChallanIds.length > 0) {
        const filteredChallans = challanData.filter((challan) => savedChallanIds.includes(challan.challan_id));
        setChallanDetailsList(filteredChallans);
      } else {
        setChallanDetailsList(challanData || []);
      }
    }
  };

  const getInvoiceDetails = async (value) => {
    const query = `${GET_INVOICE_DETAILS}/${value}`;
    const response = await getData(query);

    if (response && response?.data?.code === 200) {
      setInvoiceDetails(response?.data?.data);
      const details = response?.data?.data;

      if (details) {
        form.setFieldsValue({
          buyer_name: undefined,
          position_type: undefined,
          b2b_lc_number: undefined,
          goods_description: undefined,
          import_date: undefined,
          invoice_qty: undefined,
          invoice_number: undefined,
          invoice_value: undefined,
          sales_contract_id: undefined,
          sales_contract_number: undefined,
        });

        form.setFieldsValue({
          buyer_name: details?.buyer_name,
          position_type: details?.position_type,
          b2b_lc_number: details?.b2blc_info?.b2b_lc_number,
          goods_description: details?.goods_description,
          import_date: details?.import_date,
          invoice_qty: details?.quantity_ctn,
          invoice_number: details?.invoice_number,
          invoice_value: details?.invoice_value,
          sales_contract_id: details?.sales_contract_id,
          sales_contract_number: details?.b2blc_info?.sale_contract_info?.reference_no,
        });
      }
    }
  };

  // end local purchase reconciliation

  useEffect(() => {
    form.setFieldsValue({
      diff_qty: diffDetails?.diffQty.toFixed(2),
      diff_value: diffDetails?.diffValue.toFixed(2),
      total_challan_value: diffDetails?.totalChallanValue.toFixed(2),
      status: diffDetails?.status,
    });
  }, [diffDetails, form]);

  const getMrrNo = async () => {
    const response = await getData(GET_INVOICE_CHALLAN_MAPPING_MRR_NO);
    if (response && response?.data?.code === 200) {
      form.setFieldsValue({
        mrr_no: response?.data?.data?.mrr_no,
        formatted_mrr_no: response?.data?.data?.mrr_no
          ? `${response?.data?.data?.mrr_prefix}${response?.data?.data?.mrr_no}`
          : "",
        mrr_date: moment().format("DD-MMM-YYYY"),
      });
    }
  };

  const handleSupplierClear = async () => {
    // form.resetFields(["style"]);
    await getLocalSuppliers();
    form.resetFields(["import_details_id"]);
    setLocalSupplierInvoices([]);
    setChallanDetailsList([]);
    setAvailableChallanList([]);
    setSelectedChallanIds([]);
    setInvoiceDetails(null);
  };

  // Handle challan selection from dropdown
  const handleChallanSelect = (selectedIds) => {
    setSelectedChallanIds(selectedIds);

    // Filter challanDetailsList to show only selected challans
    if (selectedIds && selectedIds.length > 0) {
      const filteredChallans = availableChallanList.filter((challan) => selectedIds.includes(challan.challan_id));
      setChallanDetailsList(filteredChallans);
    } else {
      // If nothing selected, clear the table
      setChallanDetailsList([]);
    }
  };

  // Handle select all challans
  const handleSelectAll = (e) => {
    e.stopPropagation(); // Prevent dropdown from closing
    const allChallanIds = availableChallanList.map((challan) => challan.challan_id);
    setSelectedChallanIds(allChallanIds);
    setChallanDetailsList(availableChallanList);
  };

  // Handle deselect all challans
  const handleDeselectAll = (e) => {
    e.stopPropagation(); // Prevent dropdown from closing
    setSelectedChallanIds([]);
    setChallanDetailsList([]);
  };

  return (
    <div className="containt-body pt-3">
      <Form layout="vertical" form={form} name="control-hooks" onFinish={onFinishReconciliationForm}>
        <Card>
          <Row gutter={6}>
            <Col className="gutter-row" span={4}>
              <Form.Item label="MRR No" name="formatted_mrr_no">
                <Input style={{ width: "100%" }} disabled={true} />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4} hidden={true}>
              <Form.Item label="MRR No" name="mrr_no">
                <Input style={{ width: "100%" }} disabled={true} />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item label="MRR Date" name="mrr_date">
                <Input style={{ width: "100%" }} disabled={true} />
              </Form.Item>
            </Col>

            <Col className="gutter-row" span={4}>
              <Form.Item
                label="Local Suppliers"
                name="supplier_id"
                rules={[
                  {
                    required: true,
                    message: "Supplier is required",
                  },
                ]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Suppliers"
                  disabled={isEdit ? true : false}
                  style={{ width: "100%" }}
                  onClear={handleSupplierClear}
                  onSelect={(value) => {
                    setInvoiceDetails(null);
                    setChallanDetailsList([]);
                    setAvailableChallanList([]);
                    setSelectedChallanIds([]);
                    getSupplierInvoiceLists(value);
                    // getSupplierChallanLists(value);
                  }}
                  filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                >
                  {localSuppliers.map((option) => {
                    return (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item
                label="Invoice"
                name="import_details_id"
                rules={[
                  {
                    required: true,
                    message: "Invoice is required",
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Invoice"
                  style={{ width: "100%" }}
                  disabled={isEdit ? true : false}
                  onChange={(value, option) => {
                    setChallanDetailsList([]);
                    setAvailableChallanList([]);
                    setSelectedChallanIds([]);
                    getInvoiceDetails(value);
                    getChallanListByInvoiceSalesContract(form.getFieldValue("supplier_id"), option?.salesContractId);
                  }}
                  filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                >
                  {localSupplierInvoices.map((option) => {
                    return (
                      <option key={option.id} value={option.id} salesContractId={option?.sales_contract_id}>
                        {option.invoice_number}
                      </option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>

            <Col className="gutter-row" span={4}>
              <Form.Item label="Buyer" name="buyer_name">
                <Input style={{ width: "100%" }} disabled={true} />
              </Form.Item>
            </Col>

            <Col className="gutter-row" span={4}>
              <Form.Item label="Payment Type" name="position_type">
                <Input style={{ width: "100%" }} disabled={true} />
              </Form.Item>
            </Col>

            <Col className="gutter-row" span={4}>
              <Form.Item label="Reference No" name="b2b_lc_number">
                <Input style={{ width: "100%" }} disabled={true} />
              </Form.Item>
            </Col>

            <Col className="gutter-row" span={4} hidden={true}>
              <Form.Item label="Sales Contract Id" name="sales_contract_id">
                <Input style={{ width: "100%" }} disabled={true} />
              </Form.Item>
            </Col>

            <Col className="gutter-row" span={4}>
              <Form.Item label="Sales Contract" name="sales_contract_number">
                <Input style={{ width: "100%" }} disabled={true} />
              </Form.Item>
            </Col>

            <Col className="gutter-row" span={4}>
              <Form.Item label="Goods Description" name="goods_description">
                <Input style={{ width: "100%" }} disabled={true} />
              </Form.Item>
            </Col>

            <Col className="gutter-row" span={4}>
              <Form.Item label="Import Date" name="import_date">
                <Input style={{ width: "100%" }} disabled={true} />
              </Form.Item>
            </Col>

            <Col className="gutter-row" span={4}>
              <Form.Item label="Invoice Number" name="invoice_number">
                <Input style={{ width: "100%" }} disabled={true} />
              </Form.Item>
            </Col>

            <Col className="gutter-row" span={4}>
              <Form.Item label="Invoice Value" name="invoice_value">
                <Input style={{ width: "100%" }} disabled={true} />
              </Form.Item>
            </Col>

            <Col className="gutter-row" span={4} hidden={true}>
              <Form.Item label="Invoice Qty" name="invoice_qty">
                <Input style={{ width: "100%" }} disabled={true} />
              </Form.Item>
            </Col>

            <Col className="gutter-row" span={4} hidden={true}>
              <Form.Item label="Diff Qty" name="diff_qty">
                <Input style={{ width: "100%" }} disabled={true} />
              </Form.Item>
            </Col>
            <Col className="gutter-row" hidden span={4}>
              <Form.Item hidden label="Total Challan Value" name="total_challan_value">
                <Input style={{ width: "100%" }} disabled={true} />
              </Form.Item>
            </Col>
            <Col className="gutter-row" hidden span={4}>
              <Form.Item hidden label="Diff Value" name="diff_value">
                <Input style={{ width: "100%" }} disabled={true} />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item label="Status" name="status">
                <Input style={{ width: "100%" }} disabled={true} />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item
                label="Doc Send Date"
                name="doc_sending_date"
                rules={[
                  {
                    required: false,
                    message: "Doc Send Date is required",
                  },
                ]}
              >
                <CustomDatePicker style={{ width: "100%" }} size="small" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={8}>
              <Form.Item label="Note" name="remarks">
                <Input style={{ width: "100%" }} disabled={viewMode} />
              </Form.Item>
            </Col>

            {/* Challan Selection Dropdown */}
            {invoiceDetails && isArrayAndHasValue(availableChallanList) && (
              <Col className="gutter-row" span={12}>
                <Form.Item label="Select Challans">
                  <Select
                    mode="multiple"
                    showSearch
                    placeholder="Select challans"
                    style={{ width: "100%" }}
                    value={selectedChallanIds}
                    onChange={handleChallanSelect}
                    disabled={viewMode}
                    open={dropdownOpen}
                    onDropdownVisibleChange={setDropdownOpen}
                    filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                    dropdownRender={(menu) => (
                      <>
                        {!viewMode && isArrayAndHasValue(availableChallanList) && (
                          <div
                            style={{
                              padding: "8px 12px",
                              borderBottom: "1px solid #f0f0f0",
                              display: "flex",
                              gap: "8px",
                            }}
                          >
                            <Button
                              type="link"
                              size="small"
                              onClick={handleSelectAll}
                              style={{ padding: 0, height: "auto" }}
                            >
                              Select All
                            </Button>
                            <span style={{ color: "#d9d9d9" }}>|</span>
                            <Button
                              type="link"
                              size="small"
                              onClick={handleDeselectAll}
                              style={{ padding: 0, height: "auto" }}
                            >
                              Deselect All
                            </Button>
                          </div>
                        )}
                        {menu}
                      </>
                    )}
                  >
                    {isArrayAndHasValue(availableChallanList) &&
                      availableChallanList.map((challan) => (
                        <Option key={challan.challan_id} value={challan.challan_id}>
                          {challan.challan_no}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
            )}
          </Row>
        </Card>
        {invoiceDetails && isArrayAndHasValue(availableChallanList) && (
          <ChallanAssignTable
            challanList={challanList}
            challanDetailsList={challanDetailsList}
            setChallanDetailsList={setChallanDetailsList}
            diffDetails={diffDetails}
            setDiffDetails={setDiffDetails}
            invoiceDetails={invoiceDetails}
            isEdit={isEdit}
            viewMode={viewMode}
          />
        )}
      </Form>

      {/* Document upload section (similar to Import Shipment Status) */}
      <UploadDoc
        DOC_ADD={INVOICE_CHALLAN_MAPPING_DOC_ADD}
        setFileList={setFileList}
        filesList={filesList}
        view={viewMode}
        disableUpdateButton={false}
        disableField={false}
      />
    </div>
  );
});

export default ReconciliationForm;
